// src/components/MapboxGL/controls/TreeStatsBarControl.js

/**
 * Control que muestra la cantidad de árboles y especies en la parte
 * superior centrada del mapa. Se monta directamente sobre el contenedor
 * del mapa (no en las esquinas de Mapbox) para lograr el centrado.
 */
export const TREE_STATS_BAR_CLASS = 'tree-stats-bar-ctrl'

class TreeStatsBarControl {
  constructor(options = {}) {
    this.options = {
      treesCount: options.treesCount ?? 0,
      speciesCount: options.speciesCount ?? 0,
    }
  }

  onAdd(map) {
    this._map = map

    // Elemento real (visible), montado directamente en el contenedor del mapa
    this._wrapper = document.createElement('div')
    this._wrapper.className = TREE_STATS_BAR_CLASS
    Object.assign(this._wrapper.style, {
      position: 'absolute',
      bottom: '10px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '2',
      pointerEvents: 'auto',
    })

    this._shadow = this._wrapper.attachShadow({ mode: 'open' })
    this._renderShell()
    this._renderContent()

    map.getContainer().appendChild(this._wrapper)

    // Elemento dummy requerido por la API de Mapbox
    this._dummy = document.createElement('div')
    return this._dummy
  }

  onRemove() {
    if (this._wrapper?.parentNode) this._wrapper.parentNode.removeChild(this._wrapper)
    if (this._dummy?.parentNode) this._dummy.parentNode.removeChild(this._dummy)
    this._map = null
  }

  updateStatsFromMeta(meta) {
    if (!meta) return
    this.options.treesCount = meta.totalTrees ?? 0
    this.options.speciesCount = meta.totalSpecies ?? 0
    this._renderCounts()
  }

  updateStats(treesCount, speciesCount) {
    if (typeof treesCount === 'number') this.options.treesCount = treesCount
    if (typeof speciesCount === 'number') this.options.speciesCount = speciesCount
    this._renderCounts()
  }

  _renderShell() {
    const style = document.createElement('style')
    style.textContent = `
    :host { all: initial; }

    .bar {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-sizing: border-box;
      border-radius: 999px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      backdrop-filter: blur(6px);
      padding: 5px 14px 5px 10px;
      font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      font-size: 0.8rem;
      font-weight: 500;
      color: #111;
      white-space: nowrap;
    }

    @media (prefers-color-scheme: dark) {
      .bar {
        background: rgba(25, 25, 30, 0.85);
        color: #f0f0f0;
      }
    }

    .chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.55);
      border: 1px solid rgba(17,24,39,0.10);
      box-shadow: 0 1px 4px rgba(0,0,0,0.10);
      backdrop-filter: blur(4px);
      color: #111;
      white-space: nowrap;
    }

    @media (prefers-color-scheme: dark) {
      .chip {
        background: rgba(40, 40, 46, 0.85);
        color: #f0f0f0;
        border-color: rgba(255,255,255,0.12);
      }
    }

    .chip .num {
      font-variant-numeric: tabular-nums lining-nums;
      font-feature-settings: "tnum","lnum";
      font-weight: 800;
    }
    .chip .label { opacity: .85; }

    .divider {
      width: 1px;
      height: 18px;
      background: rgba(17,24,39,0.12);
      border-radius: 1px;
    }

    @media (prefers-color-scheme: dark) {
      .divider { background: rgba(255,255,255,0.12); }
    }
    `

    this._rootEl = document.createElement('div')
    this._rootEl.className = 'bar'
    this._shadow.append(style, this._rootEl)
  }

  _renderContent() {
    if (!this._rootEl) return

    this._rootEl.innerHTML = `
      <span class="chip" title="Árboles inventariados">
        <span aria-hidden="true">
          <svg width="16px" height="16px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M637.69 789c46.82-14.7 73.85 24.35 135.43-22.21s71.34-128.41 63.08-153.2c-16.52-64.58-60.08-84.11-63-125.54-3-41.93 41.23-57.69 20.65-120.95-26.45-81.3-65.48-61.71-71.49-70.72s15.32-27-19.83-97.32c-24.78-49.56-41.3-36-57.82-54.07s-4.5-27.36-21.78-39c-25.53-17.27-18.77 3.75-60.08-9C527.56 86 505 54.15 469.74 66.91c-82.17 29.72-55.73 87.7-72.25 105.72s-43.39 14.43-58.41 31-30 61.58-30 76.6 3 52.57 0 61.58-26.13-2.1-44.16 15.92-5.42 29.74-21.92 53.77-57.4 30.89-57.73 92.5c-0.4 75.85 33.41 81.6 26.69 111.23-7.56 33.32-35.1 84-6.56 136.59S282 840.4 340.57 838.9s39-19.53 75.1-19.53 73.59 24 139.68 10.51 53.33-31.75 82.34-40.88z" fill="#50b840"/><path d="M515.53 671.23s24.08-63.58 20.94-102.15-10.33-139-10.33-139h27.21l13 129.42 27-129.42 20 12.32s-22 86.09-31.06 150.68-17.77 129.68-17.9 146.7c0 0 29.24-14.12 57.11-28.59s59.93-56.09 71.94-56.81 20 18.31 20 22.31-57.79 53.07-76.46 63.08-41.7 14-58.72 36S546.48 839.9 547.91 892s4.43 68.09 4.43 68.09H469.9s0.34-111.14-4.67-157.2-5-83.36-12-114.77-39.48-106.62-56.79-128.59S309 475.82 296 430.07c-10-19.89 6-27.08 12-19.89s53.07 102.3 93.12 118.33c-10.49-41.05-56.07-101.13-52.07-109.14s25.91-13 34.48-5 1.56 22.72 29.6 59.42 40.34 47.93 63.73 93.38 38.67 104.06 38.67 104.06z" fill="#005866"/></svg>
        </span>
        <span class="num" id="treesNum">${this._fmt(this.options.treesCount)}</span>
        <span class="label">árboles</span>
      </span>

      <div class="divider"></div>

      <span class="chip" title="Especies registradas">
        <span aria-hidden="true">
          <svg height="16px" width="16px" viewBox="0 0 501.6 501.6" xmlns="http://www.w3.org/2000/svg">
            <path style="fill:#80BC2B;" d="M250.8,0c-12,30.4-35.2,62.4-43.2,101.6c-10.4,51.2,23.2,70.4,43.2,87.2V0z"/>
            <path style="fill:#80BC2B;" d="M250.8,0c12,30.4,35.2,62.4,43.2,101.6c10.4,51.2-23.2,70.4-43.2,87.2V0z"/>
            <path style="fill:#02243E;" d="M250.8,501.6c-2.4,0-4-1.6-4-4v-484c0-2.4,1.6-4,4-4s4,1.6,4,4v484C254.8,499.2,253.2,501.6,250.8,501.6z"/>
            <path style="fill:#80BC2B;" d="M90.8,158.4c12,28.8,18.4,66.4,40,98.4c28,41.6,64,32,88.8,29.6L90.8,158.4z"/>
            <path style="fill:#80BC2B;" d="M90.8,158.4c28.8,12,66.4,18.4,98.4,40c41.6,28,32,64,29.6,88.8L90.8,158.4z"/>
            <path style="fill:#80BC2B;" d="M410.8,120c-12,28.8-18.4,66.4-40,98.4c-28,41.6-64,32-88.8,29.6L410.8,120z"/>
            <path style="fill:#80BC2B;" d="M410.8,120c-28.8,12-66.4,18.4-98.4,40c-41.6,28-32,64-29.6,88.8L410.8,120z"/>
          </svg>
        </span>
        <span class="num" id="speciesNum">${this._fmt(this.options.speciesCount)}</span>
        <span class="label">especies</span>
      </span>
    `

    this._treesEl = this._rootEl.querySelector('#treesNum')
    this._speciesEl = this._rootEl.querySelector('#speciesNum')
  }

  _renderCounts() {
    if (this._treesEl) this._treesEl.textContent = this._fmt(this.options.treesCount)
    if (this._speciesEl)
      this._speciesEl.textContent = this._fmt(this.options.speciesCount)
  }

  _fmt(n) {
    try {
      return new Intl.NumberFormat('es-EC', { maximumFractionDigits: 0 }).format(n)
    } catch {
      return String(n)
    }
  }
}

export default TreeStatsBarControl
